import { useEffect, useRef, useCallback, type ReactNode } from 'react';
import { SidebarNav } from '@/components/shell/SidebarNav';
import type { NavItem } from '@/types/nav';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  items: NavItem[];
}

export function MobileDrawer({ open, onClose, items }: MobileDrawerProps) {
  const asideRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    touchCurrentX.current = e.touches[0].clientX;
    const diff = touchCurrentX.current - touchStartX.current;
    if (diff < 0 && asideRef.current) {
      asideRef.current.style.transform = `translateX(${diff}px)`;
      asideRef.current.style.transition = 'none';
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    const diff = touchCurrentX.current - touchStartX.current;
    if (diff < -80) {
      onClose();
    } else if (asideRef.current) {
      asideRef.current.style.transform = '';
      asideRef.current.style.transition = '';
    }
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const aside = asideRef.current;
    if (!aside) return;

    aside.addEventListener('touchstart', handleTouchStart, { passive: true });
    aside.addEventListener('touchmove', handleTouchMove, { passive: true });
    aside.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      aside.removeEventListener('touchstart', handleTouchStart);
      aside.removeEventListener('touchmove', handleTouchMove);
      aside.removeEventListener('touchend', handleTouchEnd);
    };
  }, [open, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Dark backdrop overlay - click to close */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Sidebar drawer */}
      <aside
        ref={asideRef}
        className="absolute left-0 top-0 z-10 flex h-full w-[280px] animate-slide-in-left flex-col bg-[var(--sidebar-bg)] shadow-2xl"
      >
        <div className="flex h-header shrink-0 items-center border-b border-[var(--border-subtle)] px-4">
          <span className="text-sm font-bold tracking-tight text-[var(--text-primary)]">
            SCUBE Agentic AI
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <SidebarNav items={items} onNavigate={onClose} />
        </div>
      </aside>
    </div>
  );
}

interface AppShellProps {
  portal: 'admin' | 'tenant';
  navItems: NavItem[];
  header: ReactNode;
  children: ReactNode;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function AppShell({
  portal,
  navItems,
  header,
  children,
  mobileOpen,
  onCloseMobile,
}: AppShellProps) {
  return (
    <div className="flex min-h-full bg-mesh">
      <aside className="hidden h-screen w-sidebar shrink-0 flex-col border-r border-[var(--border-subtle)] bg-[var(--sidebar-bg)] lg:flex">
        <div className="flex h-header shrink-0 items-center border-b border-[var(--border-subtle)] px-4 font-semibold text-[var(--text-primary)]">
          {portal === 'admin' ? 'Admin Portal' : 'Tenant Portal'}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <SidebarNav items={navItems} />
        </div>
      </aside>
      <MobileDrawer open={mobileOpen} onClose={onCloseMobile} items={navItems} />
      <div className="flex min-h-full min-w-0 flex-1 flex-col">
        {header}
        <main id="main-content" className="flex-1 p-3 pb-24 sm:pb-20 md:px-6 md:pt-6 md:pb-20 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
